export const LESSON_STATE = {
  FREE: "free",
  CHECKED: "checked",
  BLOCKED: "blocked"
};

export const VALIDATION_ERROR = {
  MISSING_LESSONS: "missingLessons",
  MISSING_TEXT: "missingText",
  EMPTY_ROW: "emptyRow"
};

const NEW_ROW_PREFIX = "new-";

const createDraftRow = id => ({
  id: `${NEW_ROW_PREFIX}${id}`,
  text: "",
  lessonIds: []
});

const isEmptyDraft = row => !row.text.trim() && !row.lessonIds.length;

function normalizeDrafts(edits) {
  const last = edits.newRows[edits.newRows.length - 1];
  const filled = edits.newRows.filter(row => !isEmptyDraft(row));

  if (last && isEmptyDraft(last)) {
    return { ...edits, newRows: [...filled, last] };
  }

  return {
    ...edits,
    newRows: [...filled, createDraftRow(edits.nextId)],
    nextId: edits.nextId + 1
  };
}

export function createEdits() {
  return normalizeDrafts({
    addedLessons: {},
    removedLessons: {},
    texts: {},
    deletedRows: [],
    newRows: [],
    nextId: 0
  });
}

function resolveText(row, edits) {
  return edits.texts[row.id] !== undefined ? edits.texts[row.id] : row.text;
}

function resolveLessonIds(row, edits) {
  const removed = edits.removedLessons[row.id] || [];
  const added = edits.addedLessons[row.id] || [];
  return [...row.lessonIds.filter(id => !removed.includes(id)), ...added];
}

function buildPersistedRow(row, edits) {
  const base = {
    ...row,
    isNew: false,
    originalText: row.text,
    originalLessonIds: row.lessonIds
  };

  if (edits.deletedRows.includes(row.id)) {
    return { ...base, isDeleted: true };
  }

  return {
    ...base,
    isDeleted: false,
    text: resolveText(row, edits),
    lessonIds: resolveLessonIds(row, edits)
  };
}

export function buildEditableRows(gridRows, edits) {
  const persisted = gridRows.map(row => buildPersistedRow(row, edits));

  const drafts = edits.newRows.map(row => ({
    ...row,
    isNew: true,
    isDeleted: false,
    originalLessonIds: []
  }));

  return [...persisted, ...drafts];
}

export function buildLessonOwners(rows) {
  return rows.reduce((owners, row) => {
    if (row.isDeleted) return owners;
    row.lessonIds.forEach(lessonId => {
      owners[lessonId] = row.id; // eslint-disable-line no-param-reassign
    });
    return owners;
  }, {});
}

export function getLessonState(row, lessonId, owners) {
  const owner = owners[lessonId];
  if (owner === undefined) return LESSON_STATE.FREE;
  return owner === row.id ? LESSON_STATE.CHECKED : LESSON_STATE.BLOCKED;
}

const toggleId = (ids, id) =>
  ids.includes(id) ? ids.filter(item => item !== id) : [...ids, id];

function toggleIn(map, rowId, lessonId) {
  return { ...map, [rowId]: toggleId(map[rowId] || [], lessonId) };
}

export function toggleLesson(edits, row, lessonId, owners) {
  if (
    row.isDeleted ||
    getLessonState(row, lessonId, owners) === LESSON_STATE.BLOCKED
  ) {
    return edits;
  }

  if (row.isNew) {
    return normalizeDrafts({
      ...edits,
      newRows: edits.newRows.map(draft =>
        draft.id === row.id
          ? { ...draft, lessonIds: toggleId(draft.lessonIds, lessonId) }
          : draft
      )
    });
  }

  if (row.originalLessonIds.includes(lessonId)) {
    return {
      ...edits,
      removedLessons: toggleIn(edits.removedLessons, row.id, lessonId)
    };
  }

  return {
    ...edits,
    addedLessons: toggleIn(edits.addedLessons, row.id, lessonId)
  };
}

export function updateDraftText(edits, rowId, text) {
  return normalizeDrafts({
    ...edits,
    newRows: edits.newRows.map(draft =>
      draft.id === rowId ? { ...draft, text: String(text || "") } : draft
    )
  });
}

export function updateRowText(edits, row, text) {
  const texts = { ...edits.texts };
  const value = String(text || "");
  if (value.trim() === row.originalText) {
    delete texts[row.id];
  } else {
    texts[row.id] = value;
  }
  return { ...edits, texts };
}

function releaseLessons(map, rowId, lessonIds) {
  return { ...map, [rowId]: (map[rowId] || []).filter(id => !lessonIds.includes(id)) };
}

function restoreRow(edits, row, owners) {
  const deletedRows = edits.deletedRows.filter(id => id !== row.id);
  const taken = resolveLessonIds(row, edits).filter(
    id => owners[id] !== undefined
  );
  const takenOriginal = taken.filter(id => row.lessonIds.includes(id));

  return {
    ...edits,
    deletedRows,
    addedLessons: releaseLessons(edits.addedLessons, row.id, taken),
    removedLessons: {
      ...edits.removedLessons,
      [row.id]: [
        ...new Set([...(edits.removedLessons[row.id] || []), ...takenOriginal])
      ]
    }
  };
}

export function toggleRowDeletion(edits, row, owners) {
  if (edits.deletedRows.includes(row.id)) {
    return restoreRow(edits, row, owners);
  }
  return { ...edits, deletedRows: [...edits.deletedRows, row.id] };
}

function buildRowChanges(row, edits) {
  if (edits.deletedRows.includes(row.id)) {
    return { removals: [{ text: row.text, lessonIds: row.lessonIds }] };
  }

  const removed = edits.removedLessons[row.id] || [];
  const added = edits.addedLessons[row.id] || [];
  const text = resolveText(row, edits).trim();
  const changes = { contents: [], removals: [] };

  if (removed.length) {
    changes.removals.push({ text: row.text, lessonIds: removed });
  }

  if (text !== row.text) {
    changes.contents.push({
      text,
      previousText: row.text,
      lessonIds: resolveLessonIds(row, edits)
    });
  } else if (added.length) {
    changes.contents.push({ text, lessonIds: added });
  }

  return changes;
}

export function buildSavePayload(gridRows, edits) {
  const payload = { contents: [], removals: [] };

  gridRows.forEach(row => {
    const { contents = [], removals = [] } = buildRowChanges(row, edits);
    payload.contents.push(...contents);
    payload.removals.push(...removals);
  });

  edits.newRows
    .filter(row => !isEmptyDraft(row))
    .forEach(row =>
      payload.contents.push({ text: row.text.trim(), lessonIds: row.lessonIds })
    );

  return payload;
}

export function hasPendingChanges(gridRows, edits) {
  const { contents, removals } = buildSavePayload(gridRows, edits);
  return contents.length > 0 || removals.length > 0;
}

export function needsLessons(row) {
  return (
    !row.isDeleted &&
    !row.lessonIds.length &&
    (!row.isNew || !isEmptyDraft(row))
  );
}

export function validateEdits(gridRows, edits) {
  const rows = buildEditableRows(gridRows, edits);
  const rowsWithoutLessons = rows.filter(needsLessons);
  if (rowsWithoutLessons.some(row => !row.isNew)) {
    return VALIDATION_ERROR.EMPTY_ROW;
  }
  if (rowsWithoutLessons.length) {
    return VALIDATION_ERROR.MISSING_LESSONS;
  }

  const existing = rows.filter(row => !row.isNew && !row.isDeleted);
  const drafts = edits.newRows.filter(row => !isEmptyDraft(row));
  if ([...existing, ...drafts].some(row => !row.text.trim())) {
    return VALIDATION_ERROR.MISSING_TEXT;
  }
  return null;
}
